import { ReactElement, Suspense, useState } from 'react';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  Button,
  ContextMenu,
  FullLoader,
  Pill,
  TableColumns,
  TableContainer,
  TableRows,
} from '@sc/shared-ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAlerts } from '../providers/alerts';
import { useUser } from '../providers/users';
import {
  GQL_MUTATION_REMOVE_PAYMENT_METHOD,
  GQL_QUERY_LIST_PAYMENT_METHODS,
} from '../utility/queries';
import { notify } from '../utility/notify';
import { AddPaymentMethodModal } from '../modals/AddPaymentMethodModal';
import {
  EditablePaymentMethod,
  EditPaymentMethodModal,
} from '../modals/EditPaymentMethodModal';
import { CardBrandLogo } from '../components/CardBrandLogo';

const formatBrand = (brand: string): string =>
  brand
    .split(/[\s_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const formatExpiry = (month: number, year: number): string =>
  `${String(month).padStart(2, '0')} / ${String(year).slice(-2)}`;

const joinWithAnd = (items: string[]): string =>
  items.length <= 1
    ? items[0] ?? ''
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// A card assigned to a default use can't be deleted until that use is moved to
// another card; returns the "unable to delete" copy, or null when deletable.
const blockedReason = (card: EditablePaymentMethod): string | null => {
  const uses = [card.useForMonthlyPlan && 'your monthly plan'].filter(
    (use): use is string => !!use
  );

  if (uses.length === 0) return null;

  return `This payment method is used for ${joinWithAnd(
    uses
  )}. Assign a different payment method to ${
    uses.length > 1 ? 'those' : 'that'
  } before deleting this one.`;
};

type CardActionType = 'delete' | 'edit';

const CardTableRows = (): ReactElement => {
  const { useApolloError, setAlertModal } = useAlerts();
  const onError = useApolloError();

  const { data } = useSuspenseQuery(GQL_QUERY_LIST_PAYMENT_METHODS);

  const cards = data.listPaymentMethods;

  const [editCard, setEditCard] = useState<(typeof cards)[number] | null>(null);

  const [remove] = useMutation(GQL_MUTATION_REMOVE_PAYMENT_METHOD, {
    onError,
    refetchQueries: [GQL_QUERY_LIST_PAYMENT_METHODS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      notify.success('Payment method removed.');
    },
  });

  return (
    <>
      <TableRows
        widthType="pc"
        rows={cards.map((card) => {
          const options: Array<{
            label: string;
            value: CardActionType;
            danger?: boolean;
          }> = [
            { label: 'Edit', value: 'edit' },
            { label: 'Delete', value: 'delete', danger: true },
          ];

          const pills = [card.useForMonthlyPlan && 'Monthly plan'].filter(
            (label): label is string => !!label
          );

          return {
            uuid: card.uuid,
            cells: [
              {
                width: 45,
                content: (
                  <div className="flex items-center gap-3">
                    <CardBrandLogo
                      brand={card.brand}
                      className="w-10 h-6 shrink-0"
                    />
                    <span className="text-sm text-white font-semibold">
                      {formatBrand(card.brand)} ending in {card.last4}
                    </span>
                  </div>
                ),
              },
              {
                width: 20,
                content: (
                  <span className="text-sm text-text-low-priority">
                    Expires {formatExpiry(card.expMonth, card.expYear)}
                  </span>
                ),
              },
              {
                width: 25,
                content: (
                  <div className="flex flex-wrap gap-1">
                    {pills.map((label) => (
                      <Pill
                        key={label}
                        size="small"
                        outline
                        colour="#3aa16e"
                        text={label}
                      />
                    ))}
                  </div>
                ),
              },
              {
                width: 10,
                wrapperClassname: 'justify-end',
                content: (
                  <ContextMenu<CardActionType>
                    size="medium"
                    type="basic"
                    options={options}
                    onOptionSelect={(action) => {
                      if (action === 'edit') {
                        setEditCard(card);
                        return;
                      }

                      const reason = blockedReason(card);

                      setAlertModal(
                        reason
                          ? {
                              open: true,
                              icon: 'critical',
                              title: 'Unable to delete payment method',
                              description: reason,
                            }
                          : {
                              open: true,
                              icon: 'critical',
                              isDelete: true,
                              title: 'Delete payment method',
                              description: `The card ending in ${card.last4} will be removed.`,
                              submitText: 'Delete',
                              onSubmit: () => {
                                setAlertModal(undefined);

                                void remove({
                                  variables: {
                                    input: { paymentMethodUuid: card.uuid },
                                  },
                                });
                              },
                            }
                      );
                    }}
                  />
                ),
              },
            ],
          };
        })}
      />
      <EditPaymentMethodModal
        open={!!editCard}
        card={editCard}
        onClose={() => setEditCard(null)}
      />
    </>
  );
};

export const BillingPaymentMethodsTab = (): ReactElement => {
  const { user } = useUser();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const hasActiveSubscription = !!user.company.subscription;

  return (
    <>
      <header className="flex items-center justify-end gap-3 p-5 border-b border-grey-t-800">
        {hasActiveSubscription && (
          <Button
            button={{
              text: 'Add payment method',
              size: 'small',
              type: 'secondary',
              shrink: true,
              IconStart: PlusIcon,
              onClick: () => setIsAddOpen(true),
            }}
          />
        )}
      </header>
      <TableContainer>
        <TableColumns
          widthType="pc"
          columns={[
            { width: 45, heading: 'Card' },
            { width: 20, heading: 'Expiry' },
            { width: 25, heading: 'Allocations' },
            { width: 10 },
          ]}
        />
        <Suspense fallback={<FullLoader className="size-6" />}>
          <CardTableRows />
        </Suspense>
      </TableContainer>
      <AddPaymentMethodModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
    </>
  );
};
